import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  where,
  limit,
  startAfter,
  orderBy,
  doc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';
import { useAdminPurchases } from '../hooks/AdminPurchasesContext';
import GeneralNavbar from './GeneralNavbar';
import './AdminPurchases.css';

const AdminPurchases = () => {
  const { filters, setFilters } = useAdminPurchases();
  const { selectedDate, selectedStore, purchases, lastVisible, allLoaded } = filters;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate('/admin');
  };

  const handleDetailClick = (purchaseId) => {
    navigate(`/purchase-details/${purchaseId}`);
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setFilters({
      ...filters,
      selectedDate: newDate,
      purchases: [],
      lastVisible: null,
      allLoaded: false,
    });
  };

  const handleStoreChange = (e) => {
    const newStore = e.target.value;
    setFilters({
      ...filters,
      selectedStore: newStore,
      purchases: [],
      lastVisible: null,
      allLoaded: false,
    });
  };

const fetchPurchases = useCallback(async (isLoadMore = false) => {
  if (!selectedDate) {
    setError('Por favor selecciona una fecha.');
    return;
  }

  setLoading(true);
  setError('');

  try {
    const startOfDay = new Date(`${selectedDate}T00:00:00`);
    const endOfDay = new Date(`${selectedDate}T23:59:59`);

    const filtersArray = [];

    // Asegurarse de usar el nombre correcto del campo en Firestore: 'selectedStore'
    if (selectedStore && selectedStore !== 'todas') {
      filtersArray.push(where('selectedStore', '==', selectedStore));
    }

    filtersArray.push(
      where('timestamp', '>=', startOfDay),
      where('timestamp', '<=', endOfDay),
      orderBy('timestamp', 'desc') // debe estar después de where sobre timestamp
    );

    const q = query(
      collection(db, 'compras'),
      ...filtersArray,
      ...(isLoadMore && lastVisible ? [startAfter(lastVisible)] : []),
      limit(10)
    );

    console.log('Consulta ejecutada con:', {
      selectedStore,
      selectedDate,
      startOfDay,
      endOfDay,
    });

    const querySnapshot = await getDocs(q);

    const newPurchases = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    setFilters((prev) => ({
      ...prev,
      purchases: isLoadMore ? [...prev.purchases, ...newPurchases] : newPurchases,
      lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1],
      allLoaded: querySnapshot.docs.length < 3,
    }));

    if (querySnapshot.empty && !isLoadMore) {
      setError('No se encontraron órdenes para la fecha y tienda seleccionadas.');
    }
  } catch (error) {
    console.error('Error fetching purchases:', error);
    setError('Ocurrió un error al obtener las órdenes.');
  } finally {
    setLoading(false);
  }
}, [selectedDate, selectedStore, lastVisible, setFilters]);



  const updatePurchaseStatus = async (id, status) => {
    try {
      const purchaseRef = doc(db, 'compras', id);
      await updateDoc(purchaseRef, { status });

      setFilters((prev) => ({
        ...prev,
        purchases: prev.purchases.map((purchase) =>
          purchase.id === id ? { ...purchase, status } : purchase
        ),
      }));
    } catch (error) {
      console.error('Error updating purchase status:', error);
      setError('No se pudo actualizar el estado de la orden.');
    }
  };

  useEffect(() => {
    if (selectedDate && purchases.length === 0 && !allLoaded) {
      fetchPurchases();
    }
  }, [selectedDate, selectedStore, purchases.length, allLoaded, fetchPurchases]);


  return (
    <div>
      <GeneralNavbar title="Pedidos" onBackClick={handleBackClick} />
      <div className="purchase-list-container-admin">
        <div className="filter-container-admin">
          <label htmlFor="date" className="filter-container-admin__label">
            Seleccionar Fecha:
          </label>
          <input
            type="date"
            id="date"
            className="filter-container-admin__input"
            value={selectedDate}
            onChange={handleDateChange}
          />
        
        <select value={selectedStore || ''} onChange={handleStoreChange}>
          <option value="todas">Todas</option>
          <option value="Chacabuco y Lavalle">Chacabuco y Lavalle</option>
          <option value="9 de Julio y Saavedra">9 de Julio y Saavedra</option>
          <option value="Almafuerte y Quintana">Almafuerte y Quintana</option>
        </select>
        </div>

        {error && <p className="error-text-admin">{error}</p>}

        {loading ? (
          <p className="centered-text-admin">Cargando...</p>
        ) : (
          purchases.length > 0 && (
            <>
              <table className="products-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nombre</th>
                    <th>Apellido</th>
                    <th>Email</th>
                    <th>Productos</th>
                    <th>Total</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((purchase) => (
                    <tr key={purchase.id}>
                      <td data-label="Id:">{purchase.idTracker}</td>
                      <td data-label="Nombre:">{purchase.name}</td>
                      <td data-label="Apellido:">{purchase.apellido}</td>
                      <td data-label="Email:">{purchase.email}</td>
                      <td data-label="Cantidad:">{purchase.totalItems}</td>
                      <td data-label="Total:">{purchase.totalPrice} $</td>
                      <td data-label="Estado:">{purchase.status}</td>
                      <td data-label="Fecha:">{new Date(purchase.timestamp.seconds * 1000).toLocaleString()}</td>
                      <td>
                        <button className="button-admin" onClick={() => handleDetailClick(purchase.id)}>
                          Ver Detalle
                        </button>
                        {purchase.status !== 'Completada' && purchase.status !== 'Cancelada' && (
                          <button
                            className="button-admin cancel-button-admin"
                            onClick={() => updatePurchaseStatus(purchase.id, 'Completada')}
                          >
                            Marcar como Completada
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!allLoaded && (
                <button className="detail-button-admin" onClick={() => fetchPurchases(true)}>
                  Ver más
                </button>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};

export default AdminPurchases;

